/**
 * Responsibility: Implement telemetry behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
import type { JobType } from './router'
import type { CommerceUsageMode } from '../commerce/commerce-contract'
export type AITelemetryJob = JobType | 'image'
export interface AITelemetryEntry {
  id: string
  ts: number
  jobType: AITelemetryJob
  provider: string
  mode: 'route' | 'stream'
  durationMs: number
  ok: boolean
  error?: string
  inputTokens?: number
  outputTokens?: number
  usageMode?: CommerceUsageMode
}

export interface AITelemetrySummaryRow {
  key: string
  jobType: AITelemetryJob
  provider: string
  total: number
  ok: number
  fail: number
  avgMs: number
  p95Ms: number
  inputTokens: number
  outputTokens: number
  estCostUsd: number
}

export interface AITelemetryTrendPoint {
  bucketStartTs: number
  bucketEndTs: number
  total: number
  ok: number
  fail: number
  avgMs: number
  p95Ms: number
}

export interface AICostRate {
  inputUsdPer1MToken?: number
  outputUsdPer1MToken?: number
  perRequestUsd?: number
}

export type AICostRateCard = Record<string, AICostRate>
export interface AITelemetrySnapshot {
  generatedAt: number
  entries: AITelemetryEntry[]
  summary: AITelemetrySummaryRow[]
  rateCard: AICostRateCard
  totals: {
    totalCalls: number
    successCalls: number
    failCalls: number
    inputTokens: number
    outputTokens: number
    estCostUsd: number
  }
}

const STORAGE_KEY = 'am2_ai_perf_v1'
const COST_RATE_KEY = 'am2_ai_cost_rates_v1'
const MAX_ENTRIES = 300
const DEFAULT_COST_RATE_CARD: AICostRateCard = {
  gemini: { inputUsdPer1MToken: 1.25, outputUsdPer1MToken: 5.0 },
  openai: { inputUsdPer1MToken: 5.0, outputUsdPer1MToken: 15.0 },
  anthropic: { inputUsdPer1MToken: 3.0, outputUsdPer1MToken: 15.0 },
  groq: { inputUsdPer1MToken: 0.2, outputUsdPer1MToken: 0.2 },
  ollama: { inputUsdPer1MToken: 0.0, outputUsdPer1MToken: 0.0 },
  gemini_imagen: { perRequestUsd: 0.04 },
}
let cache: AITelemetryEntry[] | null = null
const listeners = new Set<() => void>()
function loadEntries(): AITelemetryEntry[] {
  if (cache) return cache
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    cache = Array.isArray(parsed) ? parsed : []
  } catch {
    cache = []
  }
  return cache
}

function persist(entries: AITelemetryEntry[]): void {
  cache = entries
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // Non-fatal on quota issues.
  }
}

function emit(): void {
  for (const fn of listeners) fn()
}

export function onAITelemetryChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getAITelemetry(): AITelemetryEntry[] {
  return [...loadEntries()]
}

export function clearAITelemetry(): void {
  persist([])
  emit()
}

function safeInt(n: unknown): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0
  return Math.max(0, Math.round(n))
}

export function estimateTokensFromChars(charCount: number): number {
  const chars = Math.max(0, Math.round(charCount))
  return Math.ceil(chars / 4)
}

export function estimateTokensFromText(text: string): number {
  return estimateTokensFromChars((text ?? '').length)
}

export function recordAITelemetry(data: Omit<AITelemetryEntry, 'id' | 'ts'>): void {
  const entries = loadEntries()
  const next: AITelemetryEntry = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    ts: Date.now(),
    ...data,
    durationMs: Math.max(0, Math.round(data.durationMs)),
    inputTokens: safeInt(data.inputTokens),
    outputTokens: safeInt(data.outputTokens),
    ...(data.usageMode ? { usageMode: data.usageMode } : {}),
  }
  const merged = [next, ...entries].slice(0, MAX_ENTRIES)
  persist(merged)
  emit()
}

function percentile(values: number[], p: number): number {
  if (!values.length) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))
  return sorted[idx] ?? 0
}

function loadCustomCostRates(): AICostRateCard {
  try {
    const raw = localStorage.getItem(COST_RATE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return (parsed && typeof parsed === 'object') ? parsed : {}
  } catch {
    return {}
  }
}

export function getAICostRateCard(): AICostRateCard {
  return { ...DEFAULT_COST_RATE_CARD, ...loadCustomCostRates() }
}

export function setAICostRateCard(card: AICostRateCard): void {
  try {
    localStorage.setItem(COST_RATE_KEY, JSON.stringify(card))
  } catch {
    // Non-fatal on quota issues.
  }
  emit()
}

export function estimateEntryCostUsd(
  entry: Pick<AITelemetryEntry, 'provider' | 'inputTokens' | 'outputTokens'>,
  rateCard: AICostRateCard = getAICostRateCard(),
): number {
  const rate = rateCard[entry.provider] ?? {}
  const inTok = safeInt(entry.inputTokens)
  const outTok = safeInt(entry.outputTokens)
  const inCost = (inTok / 1_000_000) * (rate.inputUsdPer1MToken ?? 0)
  const outCost = (outTok / 1_000_000) * (rate.outputUsdPer1MToken ?? 0)
  const reqCost = rate.perRequestUsd ?? 0
  return inCost + outCost + reqCost
}

export function summarizeAITelemetry(
  entries: AITelemetryEntry[] = getAITelemetry(),
  rateCard: AICostRateCard = getAICostRateCard(),
): AITelemetrySummaryRow[] {
  const byKey = new Map<string, AITelemetrySummaryRow & { durations: number[] }>()
  for (const e of entries) {
    const key = `${e.jobType}::${e.provider}`
    const row = byKey.get(key) ?? {
      key,
      jobType: e.jobType,
      provider: e.provider,
      total: 0,
      ok: 0,
      fail: 0,
      avgMs: 0,
      p95Ms: 0,
      inputTokens: 0,
      outputTokens: 0,
      estCostUsd: 0,
      durations: [],
    }
    row.total += 1
    if (e.ok) row.ok += 1
    else row.fail += 1
    row.durations.push(e.durationMs)
    row.inputTokens += safeInt(e.inputTokens)
    row.outputTokens += safeInt(e.outputTokens)
    row.estCostUsd += estimateEntryCostUsd(e, rateCard)
    byKey.set(key, row)
  }

  return [...byKey.values()]
    .map(row => {
      const totalDur = row.durations.reduce((a, b) => a + b, 0)
      return {
        key: row.key,
        jobType: row.jobType,
        provider: row.provider,
        total: row.total,
        ok: row.ok,
        fail: row.fail,
        avgMs: row.total ? Math.round(totalDur / row.total) : 0,
        p95Ms: Math.round(percentile(row.durations, 95)),
        inputTokens: row.inputTokens,
        outputTokens: row.outputTokens,
        estCostUsd: Number(row.estCostUsd.toFixed(6)),
      }
    })
    .sort((a, b) => b.total - a.total)
}

export function buildAITelemetryTrend(
  entries: AITelemetryEntry[] = getAITelemetry(),
  bins = 12,
): AITelemetryTrendPoint[] {
  if (!entries.length || bins <= 0) return []
  const sorted = [...entries].sort((a, b) => a.ts - b.ts)
  const minTs = sorted[0].ts
  const maxTs = sorted[sorted.length - 1].ts
  const range = Math.max(1, maxTs - minTs)
  const bucketSize = Math.max(1, Math.ceil(range / bins))
  const points: Array<AITelemetryTrendPoint & { durations: number[] }> = Array.from({ length: bins }, (_, i) => ({
    bucketStartTs: minTs + (i * bucketSize),
    bucketEndTs: minTs + ((i + 1) * bucketSize),
    total: 0,
    ok: 0,
    fail: 0,
    avgMs: 0,
    p95Ms: 0,
    durations: [],
  }))
  for (const e of sorted) {
    const idx = Math.min(points.length - 1, Math.max(0, Math.floor((e.ts - minTs) / bucketSize)))
    const p = points[idx]
    p.total += 1
    if (e.ok) p.ok += 1
    else p.fail += 1
    p.durations.push(e.durationMs)
  }

  return points
    .map(p => ({
      bucketStartTs: p.bucketStartTs,
      bucketEndTs: p.bucketEndTs,
      total: p.total,
      ok: p.ok,
      fail: p.fail,
      avgMs: p.total ? Math.round(p.durations.reduce((a, b) => a + b, 0) / p.total) : 0,
      p95Ms: Math.round(percentile(p.durations, 95)),
    }))
    .filter(p => p.total > 0)
}

export function buildAITelemetrySnapshot(
  entries: AITelemetryEntry[] = getAITelemetry(),
  rateCard: AICostRateCard = getAICostRateCard(),
): AITelemetrySnapshot {
  const summary = summarizeAITelemetry(entries, rateCard)
  return {
    generatedAt: Date.now(),
    entries: [...entries],
    summary,
    rateCard: { ...rateCard },
    totals: {
      totalCalls: entries.length,
      successCalls: entries.filter(e => e.ok).length,
      failCalls: entries.filter(e => !e.ok).length,
      inputTokens: entries.reduce((sum, e) => sum + safeInt(e.inputTokens), 0),
      outputTokens: entries.reduce((sum, e) => sum + safeInt(e.outputTokens), 0),
      estCostUsd: Number(summary.reduce((sum, row) => sum + row.estCostUsd, 0).toFixed(6)),
    },
  }
}
